import { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Carousel from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { getImagesInFolder } from "../api/api";

interface Props {
  year: string;
  eventType: string;
  folders: string[];
}

const CelebrationGrid = ({ year, eventType, folders }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [thumbnails, setThumbnails] = useState<Record<string, string | null>>(
    {},
  );
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");

  const validFolders = folders.filter((f) => f.trim() !== "");

  useEffect(() => {
    if (!validFolders.length) return;

    setThumbnails({});

    validFolders.forEach(async (folder) => {
      const images = await getImagesInFolder(year, eventType, folder);
      setThumbnails((prev) => ({
        ...prev,
        [folder]: images[0] ?? null,
      }));
    });
  }, [year, validFolders.join(",")]);

  const handleCardClick = async (folder: string) => {
    const images = await getImagesInFolder(year, eventType, folder);
    setModalImages(images as string[]);
    setModalTitle(folder);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setModalImages([]);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {validFolders.map((folder) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={folder}>
            <Card
              onClick={() => handleCardClick(folder)}
              sx={{
                cursor: "pointer",
                height: "100%",
                borderRadius: "10px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
                transition: "transform 0.18s, box-shadow 0.18s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
                },
                overflow: "hidden",
              }}
            >
              {thumbnails[folder] === undefined ? (
                <Box
                  sx={{
                    height: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  height="200"
                  image={thumbnails[folder] as string}
                  alt={folder}
                  sx={{ objectFit: "cover" }}
                />
              )}
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography
                  variant="subtitle1"
                  title={folder}
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    lineHeight: 1.3,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {folder}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: "12px" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            px: 6,
            py: 1.5,
            flexShrink: 0,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              textAlign: "center",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {modalTitle}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Image area fills remaining height */}
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "#000",
          }}
        >
          <style>{`
            .custom-carousel, .custom-carousel .carousel, .custom-carousel .carousel .slider-wrapper, .custom-carousel .carousel .slider {
              height: 100%;
            }
            .custom-carousel .carousel .slide {
              display: flex;
              align-items: center;
              justify-content: center;
              background: #000;
            }
            .custom-carousel .carousel .control-arrow {
              top: 50%;
              transform: translateY(-50%);
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: rgba(0, 0, 0, 0.55) !important;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 1 !important;
              margin: 0 8px;
              transition: background 0.2s;
            }
            .custom-carousel .carousel .control-arrow:hover {
              background: rgba(0, 0, 0, 0.82) !important;
            }
            .custom-carousel .carousel .control-prev.control-arrow::before {
              border-right-color: #fff !important;
              border-width: 10px 14px 10px 0 !important;
              margin: 0;
            }
            .custom-carousel .carousel .control-next.control-arrow::before {
              border-left-color: #fff !important;
              border-width: 10px 0 10px 14px !important;
              margin: 0;
            }
          `}</style>
          <div
            className="custom-carousel"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <Carousel.Carousel
              showArrows={true}
              dynamicHeight={false}
              showThumbs={false}
            >
              {modalImages.map((src, index) => (
                <div key={index}>
                  <img
                    src={src}
                    alt={`${modalTitle} ${index + 1}`}
                    style={{
                      width: "100%",
                      height: isMobile ? "calc(100dvh - 57px)" : "80vh",
                      objectFit: "contain",
                    }}
                  />
                </div>
              ))}
            </Carousel.Carousel>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CelebrationGrid;
